import React from 'react'
import type { ColumnsType } from 'antd/es/table'
import { formatDateForExport, getRawValueKey } from '../../../../../core/utils/exportFormatUtils.ts'
import { FieldTypeEnum, type FieldValue } from '../../../../../core/components/InputManager/types.ts'
import { getDisplayValue } from '../../../../../core/components/InputManager/utils/displayValue.tsx'

export type HoldingsFieldType = 'number' | 'currency' | 'valuation' | 'date' | 'text'

export interface HoldingsColumnConfig {
  columnTitle: string
  valueKey: string
  width: number
  fieldType?: HoldingsFieldType
  /** For valuation: key to get Pre/Post type from record (e.g. prog__valuation_type) */
  valuationTypeKey?: string
  /** Record link: module for openFormInTab (e.g. 'tranches', 'asset-transactions') */
  linkModule?: string
  /** Record link: row key holding the target id (e.g. 'tranches__id_targeted') */
  linkIdKey?: string
}

const NUMERIC_FIELD_TYPES: HoldingsFieldType[] = ['number', 'currency', 'valuation']

const holdingsColumnsConfig: HoldingsColumnConfig[] = [
  { columnTitle: 'Fund', valueKey: 'funds__name', width: 150, linkModule: 'funds', linkIdKey: 'funds__id' },
  { columnTitle: 'Security', valueKey: 'securities__name', width: 150, linkModule: 'securities', linkIdKey: 'securities__id' },
  { columnTitle: 'Cost Basis', valueKey: 'cost_basis_fmt', width: 120, fieldType: 'currency' },
  { columnTitle: 'Realized Value', valueKey: 'realized_value_fmt', width: 130, fieldType: 'currency' },
  { columnTitle: 'Unrealized Value', valueKey: 'unrealized_value_fmt', width: 140, fieldType: 'currency' },
  { columnTitle: 'Total Value', valueKey: 'total_value_fmt', width: 120, fieldType: 'currency' },
  { columnTitle: 'MOIC', valueKey: 'moic_fmt', width: 80, fieldType: 'number' },
  { columnTitle: 'Percentage Exited', valueKey: 'perc_exited_fmt', width: 120, fieldType: 'number' },
  { columnTitle: 'Unit Price', valueKey: 'unit_price_fmt', width: 100, fieldType: 'currency' },
  { columnTitle: 'No. Held', valueKey: 'no_held_fmt', width: 90, fieldType: 'number' },
  { columnTitle: 'Principal Held', valueKey: 'principal_held_fmt', width: 120, fieldType: 'currency' },
  { columnTitle: 'Cost Disposed', valueKey: 'cost_disposed_fmt', width: 120, fieldType: 'currency' },
  { columnTitle: 'Realized Gain', valueKey: 'realized_gain_fmt', width: 120, fieldType: 'currency' },
  // the two non-cash projections of the asset-delta design (2026-10-21):
  // the gain or loss recognised without proceeds (part of Realized Gain)
  // and the basis that arrived without cash (part of Cost Held)
  { columnTitle: 'Gain Recognised', valueKey: 'gain_recognized_fmt', width: 130, fieldType: 'currency' },
  { columnTitle: 'Cost Held', valueKey: 'cost_held_fmt', width: 100, fieldType: 'currency' },
  { columnTitle: 'Cost Adjusted', valueKey: 'cost_adjusted_fmt', width: 120, fieldType: 'currency' },
  { columnTitle: 'Unrealized Gain', valueKey: 'unrealized_gain_fmt', width: 130, fieldType: 'currency' }
]

const transactionSummaryColumnsConfig: HoldingsColumnConfig[] = [
  {
    columnTitle: 'Date',
    valueKey: 'date_fmt',
    width: 110,
    fieldType: 'date',
    linkModule: 'asset-transactions',
    linkIdKey: 'asset_transactions__id'
  },
  { columnTitle: 'Asset Transaction Type', valueKey: 'cfg_asset_transaction_types__label', width: 160 },
  { columnTitle: 'Fund', valueKey: 'funds__name', width: 150, linkModule: 'funds', linkIdKey: 'funds__id' },
  { columnTitle: 'Security', valueKey: 'securities__name', width: 150, linkModule: 'securities', linkIdKey: 'securities__id' },
  { columnTitle: 'Amount', valueKey: 'amount_fmt', width: 120, fieldType: 'currency' },
  { columnTitle: 'Round', valueKey: 'rounds__name', width: 120, linkModule: 'rounds', linkIdKey: 'rounds__id' },
  {
    columnTitle: 'Valuation',
    valueKey: 'prog__valuation',
    width: 120,
    fieldType: 'valuation',
    valuationTypeKey: 'prog__valuation_type'
  },
  { columnTitle: 'Unit Price', valueKey: 'unit_price_fmt', width: 100, fieldType: 'currency' },
  { columnTitle: 'Units Acquired', valueKey: 'units_acquired_fmt', width: 120, fieldType: 'number' },
  { columnTitle: 'Units Disposed', valueKey: 'units_disposed_fmt', width: 120, fieldType: 'number' },
  { columnTitle: 'Split Factor', valueKey: 'split_factor_fmt', width: 100, fieldType: 'number' },
  {
    columnTitle: 'Tranche Targeted',
    valueKey: 'tranches__name_targeted',
    width: 150,
    linkModule: 'tranches',
    linkIdKey: 'tranches__id_targeted'
  },
  {
    columnTitle: 'Tranche Created',
    valueKey: 'tranches__name_created',
    width: 150,
    linkModule: 'tranches',
    linkIdKey: 'tranches__id_created'
  }
]

const transactionDetailColumnsConfig: HoldingsColumnConfig[] = [
  { columnTitle: 'Tranche', valueKey: 'tranches__name', width: 180, linkModule: 'tranches', linkIdKey: 'tranches__id' },
  { columnTitle: 'Fund', valueKey: 'funds__name', width: 150, linkModule: 'funds', linkIdKey: 'funds__id' },
  { columnTitle: 'Security', valueKey: 'securities__name', width: 150, linkModule: 'securities', linkIdKey: 'securities__id' },
  {
    columnTitle: 'Date',
    valueKey: 'transaction_date_fmt',
    width: 110,
    fieldType: 'date',
    linkModule: 'asset-transactions',
    linkIdKey: 'asset_transactions__id'
  },
  { columnTitle: 'Asset Transaction Type', valueKey: 'cfg_asset_transaction_types__label', width: 180 },
  { columnTitle: 'Cost Incurred', valueKey: 'cost_incurred_fmt', width: 120, fieldType: 'currency' },
  { columnTitle: 'Cost Held', valueKey: 'cost_held_fmt', width: 100, fieldType: 'currency' },
  { columnTitle: 'Cost Disposed', valueKey: 'cost_disposed_fmt', width: 120, fieldType: 'currency' },
  { columnTitle: 'Realized Value', valueKey: 'realized_value_fmt', width: 130, fieldType: 'currency' },
  { columnTitle: 'Unrealized Value', valueKey: 'unrealized_value_fmt', width: 140, fieldType: 'currency' },
  { columnTitle: 'Total Value', valueKey: 'total_value_fmt', width: 120, fieldType: 'currency' },
  { columnTitle: 'Units Held', valueKey: 'units_held_fmt', width: 100, fieldType: 'number' },
  { columnTitle: 'Units Disposed', valueKey: 'units_disposed_fmt', width: 120, fieldType: 'number' },
  { columnTitle: 'Realized Gain', valueKey: 'realized_gain_fmt', width: 120, fieldType: 'currency' },
  { columnTitle: 'Unrealized Gain', valueKey: 'unrealized_gain_fmt', width: 130, fieldType: 'currency' },
  { columnTitle: 'Unit Price', valueKey: 'unit_price_fmt', width: 100, fieldType: 'currency' },
  { columnTitle: 'Delta Cost Incurred', valueKey: 'delta_cost_incurred_fmt', width: 160, fieldType: 'currency' },
  { columnTitle: 'Delta Cost Held', valueKey: 'delta_cost_held_fmt', width: 160, fieldType: 'currency' },
  { columnTitle: 'Delta Cost Disposed', valueKey: 'delta_cost_disposed_fmt', width: 160, fieldType: 'currency' },
  { columnTitle: 'Delta Realized Value', valueKey: 'delta_realized_value_fmt', width: 160, fieldType: 'currency' },
  {
    columnTitle: 'Delta Unrealized Value',
    valueKey: 'delta_unrealized_value_fmt',
    width: 170,
    fieldType: 'currency'
  },
  { columnTitle: 'Delta Total Value', valueKey: 'delta_total_value_fmt', width: 160, fieldType: 'currency' },
  { columnTitle: 'Delta Units Held', valueKey: 'delta_units_held_fmt', width: 160, fieldType: 'number' },
  { columnTitle: 'Delta Units Disposed', valueKey: 'delta_units_disposed_fmt', width: 160, fieldType: 'number' },
  { columnTitle: 'Delta Realized Gain', valueKey: 'delta_realized_gain_fmt', width: 160, fieldType: 'currency' },
  { columnTitle: 'Delta Unrealized Gain', valueKey: 'delta_unrealized_gain_fmt', width: 170, fieldType: 'currency' }
]

// House record-link styling (same wrapper the reporting grid tables use).
const RECORD_LINK_CLASSES =
  'cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'

export function configToColumns(
  config: HoldingsColumnConfig[],
  openRecord?: (module: string, id: string, title: string) => void
): ColumnsType<Record<string, unknown> & { key: string }> {
  return config.map((col) => {
    const displayValue = (val: unknown) => (val != null && val !== '' ? String(val) : '-')
    const isNumeric = col.fieldType && NUMERIC_FIELD_TYPES.includes(col.fieldType)
    const isValuation = col.fieldType === 'valuation' && col.valuationTypeKey

    return {
      title: col.columnTitle,
      dataIndex: col.valueKey,
      key: col.valueKey,
      width: col.width,
      render: (val: FieldValue, record: Record<string, unknown>) => {
        if (isValuation) {
          const valuationType = record[col.valuationTypeKey!] as 'Pre' | 'Post' | undefined
          return getDisplayValue(
            {
              key: col.valueKey,
              type: FieldTypeEnum.VALUATION,
              value: val,
              valuationType: valuationType || undefined,
              showValuationType: true
            },
            { alignment: 'right', labelPosition: 'inline' }
          )
        }
        if (isNumeric) {
          return React.createElement('div', { className: 'text-right' }, displayValue(val))
        }
        // Record link (Fund / Security / Round / Tranche / Date->transaction):
        // only when the row actually carries the target id (e.g. the Current
        // Holdings "Total" rollup row has NULL ids and stays plain text).
        const linkId = col.linkModule && col.linkIdKey ? record[col.linkIdKey] : null
        if (openRecord && col.linkModule && linkId && val != null && val !== '') {
          return React.createElement(
            'button',
            {
              type: 'button',
              className: RECORD_LINK_CLASSES,
              onClick: () => openRecord(col.linkModule!, String(linkId), String(val))
            },
            String(val)
          )
        }
        return displayValue(val)
      }
    }
  })
}

export { holdingsColumnsConfig, transactionSummaryColumnsConfig, transactionDetailColumnsConfig }

export function prepareDataForExport(
  rows: Record<string, unknown>[],
  columnConfig: HoldingsColumnConfig[]
): { data: Record<string, unknown>[]; columns: string[]; dateColumns: string[] } {
  const columns: string[] = []
  for (const col of columnConfig) {
    columns.push(col.columnTitle)
    if (col.valuationTypeKey) {
      columns.push(`${col.columnTitle} Type`)
    }
  }
  const dateColumns = columnConfig.filter((c) => c.fieldType === 'date').map((c) => c.columnTitle)
  const data = rows.map((record) => {
    const exportRow: Record<string, unknown> = {}
    for (const col of columnConfig) {
      const rawKey = getRawValueKey(col.valueKey)
      let value = record[rawKey] ?? record[col.valueKey]
      if (col.fieldType === 'date') {
        value = formatDateForExport(value)
      }
      exportRow[col.columnTitle] = value
      if (col.valuationTypeKey) {
        exportRow[`${col.columnTitle} Type`] = record[col.valuationTypeKey]
      }
    }
    return exportRow
  })
  return { data, columns, dateColumns }
}
